import React, { useState } from 'react';
import Input from './Input';
import Button from './Button';
import { formatDateOnlyWithWeekdayInTimezone } from '../utils/timezone';

interface DateExceptionsPickerProps {
  exceptions: Date[];
  onChange: (exceptions: Date[]) => void;
  label?: string;
}

const DateExceptionsPicker: React.FC<DateExceptionsPickerProps> = ({
  exceptions,
  onChange,
  label = 'Exception Dates',
}) => {
  const [newDate, setNewDate] = useState('');

  const addException = () => {
    if (!newDate) return;

    const date = new Date(newDate);
    // Check if date is valid
    if (isNaN(date.getTime())) return;

    // Check if date already exists
    const dateStr = date.toISOString().split('T')[0];
    const alreadyExists = exceptions.some(
      (d) => new Date(d).toISOString().split('T')[0] === dateStr
    );

    if (alreadyExists) return;

    onChange([...exceptions, date].sort((a, b) => new Date(a).getTime() - new Date(b).getTime()));
    setNewDate('');
  };

  const removeException = (index: number) => {
    onChange(exceptions.filter((_, i) => i !== index));
  };

  const formatDate = (date: Date) => {
    return formatDateOnlyWithWeekdayInTimezone(new Date(date), 'UTC');
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>

      {exceptions.length > 0 && (
        <div className="mb-3 space-y-2">
          {exceptions.map((exception, index) => (
            <div
              key={index}
              className="flex items-center justify-between bg-gray-50 p-2 rounded-lg"
            >
              <span className="text-sm text-gray-700">{formatDate(exception)}</span>
              <button
                type="button"
                onClick={() => removeException(index)}
                className="text-red-600 hover:text-red-700 text-sm font-medium"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          id="exception-date"
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          placeholder="Select a date"
        />
        <Button type="button" onClick={addException} size="sm" disabled={!newDate}>
          Add
        </Button>
      </div>

      <p className="text-xs text-gray-500 mt-1">
        Add dates when this window should not be available
      </p>
    </div>
  );
};

export default DateExceptionsPicker;

